import { z } from "zod";

const IMAGE_FILE_EXTENSIONS: ReadonlySet<string> = new Set(["png", "jpg", "jpeg"]);

export const ChatMessageFileSchema = z.object({
  attachment_id: z.string(),
  file_name: z.string(),
  storage_url: z.string(),
});

export type ChatMessageFile = z.infer<typeof ChatMessageFileSchema>;

export const USER_TURN_FILES_EVENT_TYPE = "user_turn_files";

export const UserTurnFilesEventSchema = z.object({
  type: z.literal(USER_TURN_FILES_EVENT_TYPE),
  files: z.array(ChatMessageFileSchema),
});

const UserTurnArtifactsSchema = z.object({
  files: z.array(ChatMessageFileSchema).default([]),
});

export function userTurnFiles(turnArtifacts: unknown): ChatMessageFile[] {
  return UserTurnArtifactsSchema.parse(turnArtifacts ?? {}).files;
}

const UserTurnAttachmentSnapshotSchema = z.object({
  conversation_attachment_ids: z.array(z.string()).optional(),
});

export interface ConversationAttachmentRef {
  id: string;
  fileName: string;
  storageUrl?: string | null;
}

export interface SentFilesTurnArtifacts {
  files: ChatMessageFile[];
  conversation_attachment_ids: string[];
}

export function unsentAttachments<Attachment extends ConversationAttachmentRef>(
  attachments: Attachment[],
  lastUserTurnArtifacts: unknown,
): Attachment[] {
  const parsedSnapshot = UserTurnAttachmentSnapshotSchema.safeParse(lastUserTurnArtifacts ?? {});
  const attachmentIdsAtLastTurn = parsedSnapshot.success ? parsedSnapshot.data.conversation_attachment_ids : undefined;
  if (attachmentIdsAtLastTurn === undefined) return attachments;
  const alreadySentIds = new Set(attachmentIdsAtLastTurn);
  return attachments.filter((attachment) => !alreadySentIds.has(attachment.id));
}

export function alreadySentAttachmentIds(
  attachments: ConversationAttachmentRef[],
  lastUserTurnArtifacts: unknown,
): ReadonlySet<string> {
  const unsentIds = new Set(unsentAttachments(attachments, lastUserTurnArtifacts).map((attachment) => attachment.id));
  return new Set(attachments.map((attachment) => attachment.id).filter((attachmentId) => !unsentIds.has(attachmentId)));
}

export function sentFilesTurnArtifacts(
  attachments: ConversationAttachmentRef[],
  lastUserTurnArtifacts: unknown,
): SentFilesTurnArtifacts {
  const files = unsentAttachments(attachments, lastUserTurnArtifacts).flatMap((attachment) =>
    attachment.storageUrl
      ? [{ attachment_id: attachment.id, file_name: attachment.fileName, storage_url: attachment.storageUrl }]
      : [],
  );
  return { files, conversation_attachment_ids: attachments.map((attachment) => attachment.id) };
}

export function isImageFileName(fileName: string): boolean {
  return IMAGE_FILE_EXTENSIONS.has(fileExtension(fileName));
}

export function fileExtension(fileName: string): string {
  return fileName.split(".").pop()?.toLowerCase() ?? "";
}
